import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { tFamily } from "@/i18n";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Curated bestsellers carousel. Fetches from /api/products/bestsellers,
 * shows 4 tiles per view on desktop, 2 on mobile, with prev/next controls.
 */
export default function BestsellersCarousel() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/products/bestsellers?limit=12").then(({ data }) => setItems(data));
  }, []);

  if (!items.length) return null;

  const perView = 4;
  const maxIdx = Math.max(0, items.length - perView);
  const prev = () => setIdx((i) => Math.max(0, i - 1));
  const next = () => setIdx((i) => Math.min(maxIdx, i + 1));

  return (
    <section className="py-20 md:py-28" data-testid="home-bestsellers-carousel">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-mono-caps text-muted-foreground">{t("home.best_eyebrow")}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-2">{t("home.best_title")}</h2>
            <p className="text-muted-foreground mt-3 max-w-md text-sm leading-relaxed">
              {t("home.best_copy")}
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={prev}
              disabled={idx === 0}
              className="p-3 border border-navy text-navy disabled:opacity-30 hover:bg-navy hover:text-cream transition-colors"
              data-testid="bestsellers-prev"
              aria-label="Anterior"
            >
              <ChevronLeft strokeWidth={1.25} className="h-5 w-5" />
            </button>
            <button
              onClick={next}
              disabled={idx >= maxIdx}
              className="p-3 border border-navy text-navy disabled:opacity-30 hover:bg-navy hover:text-cream transition-colors"
              data-testid="bestsellers-next"
              aria-label="Siguiente"
            >
              <ChevronRight strokeWidth={1.25} className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${idx * (100 / perView)}%)` }}
          >
            {items.map((p, i) => {
              const cheapest = p.sizes.reduce((a, b) => (a.price <= b.price ? a : b));
              const price = cheapest.price;
              const compareAt = cheapest.compare_at_price;
              const onSale = compareAt && compareAt > price;
              return (
                <Link
                  key={p.id}
                  to={`/producto/${p.slug}`}
                  className="group flex-shrink-0 w-1/2 md:w-1/4 pr-6 md:pr-8"
                  data-testid={`bestseller-${p.slug}`}
                >
                  <div className="relative bg-white aspect-square mb-4 overflow-hidden">
                    <span className="absolute top-3 left-3 z-10 font-mono-caps bg-royal text-white text-[10px] px-2 py-1">
                    #{i + 1} {t("home.best_eyebrow").split(" · ")[1] || "Bestseller"}
                  </span>
                    <img
                      src={p.images[0]}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-contain p-2 transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="font-mono-caps text-muted-foreground">{p.brand}</div>
                  <h3 className="font-serif text-xl leading-tight">{p.name}</h3>
                  <div className="flex items-baseline justify-between mt-1">
                    <div className="text-sm text-muted-foreground">
                      {p.concentration} · {p.family}
                    </div>
                    <div className="font-medium flex items-baseline gap-2">
                      {onSale && (
                        <span className="text-xs text-muted-foreground line-through">
                          ${compareAt.toFixed(2)}
                        </span>
                      )}
                      <span className={onSale ? "text-[#8B0000]" : ""}>Desde ${price.toFixed(2)}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="md:hidden flex items-center gap-2 justify-center mt-8">
          <button
            onClick={prev}
            disabled={idx === 0}
            className="p-3 border border-navy text-navy disabled:opacity-30"
            data-testid="bestsellers-prev-mobile"
            aria-label="Anterior"
          >
            <ChevronLeft strokeWidth={1.25} className="h-5 w-5" />
          </button>
          <button
            onClick={next}
            disabled={idx >= maxIdx}
            className="p-3 border border-navy text-navy disabled:opacity-30"
            data-testid="bestsellers-next-mobile"
            aria-label="Siguiente"
          >
            <ChevronRight strokeWidth={1.25} className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
}
